import React, { useState, useEffect } from 'react';
import '../styles/sculptd-theme.css';
import { Menu, X } from 'lucide-react';

// import { HomeDashboardPanel } from '../components/exercise-app/HomeDashboardPanel';
// import { OnboardingPanel } from '../components/exercise-app/OnboardingPanel';
import WorkoutPlayerPanel from '../components/exercise-app/WorkoutPlayerPanel';
import ExerciseLibraryPanel from '../components/exercise-app/ExerciseLibraryPanel';
import { WorkoutRecordingPanel } from '../components/exercise-app/WorkoutRecordingPanel';
import { CommunityPanel } from '../components/exercise-app/CommunityPanel';
import SocialMediaPanel from '../components/exercise-app/SocialMediaPanel';
import HealthDataPanel from '../components/exercise-app/HealthDataPanel';
import { PhotoProgressPanel } from '../components/exercise-app/PhotoProgressPanel';
// import { MotivationalPanel } from '../components/exercise-app/MotivationalPanel';
import { SomaChatPanel } from '../components/exercise-app/SomaChatPanel';
import GamificationPanel from '../components/exercise-app/GamificationPanel';
import AdminDashboardPanel from '../components/exercise-app/AdminDashboardPanel';
import { DeviceIntegrationPanel } from '../components/exercise-app/DeviceIntegrationPanel';
import { WearableVerificationPlugin } from '../components/exercise-app/WearableVerificationPlugin';
import { RecoveryVerificationPlugin } from '../components/exercise-app/RecoveryVerificationPlugin';
import WeightManagementPanel from '../components/exercise-app/WeightManagementPanel';
import StorePanel from '../components/exercise-app/StorePanel';

const ErrorBoundary = ({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) => {
  return <div>{children}</div>;
};

const LoadingSpinner = ({ size, variant, text }: { size?: string; variant?: string; text?: string }) => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">{text || 'Loading...'}</p>
    </div>
  </div>
);

const DemoModeToggle = () => (
  <div className="text-sm px-3 py-1 rounded-full bg-blue-100 text-blue-800">
    Demo Mode
  </div>
);

const isDemoMode = () => true;
const logDemoAction = (action: string, data: any) => console.log('Demo:', action, data);

const FitnessDemo: React.FC = () => {
  const [activeComponent, setActiveComponent] = useState<string>('workout-player');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(true);
  const [showDemoBanner, setShowDemoBanner] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      setShowDemoBanner(isDemoMode());
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isDemoMode()) {
      logDemoAction('Fitness demo loaded', {
        component: activeComponent,
        timestamp: new Date().toISOString()
      });
    }
  }, [activeComponent]);

  const components = [
    // { id: 'dashboard', name: 'Home Dashboard', component: HomeDashboardPanel },
    // { id: 'onboarding', name: 'Onboarding', component: OnboardingPanel },
    { id: 'workout-player', name: 'Workout Player', component: WorkoutPlayerPanel },
    { id: 'exercise-library', name: 'Exercise Library', component: ExerciseLibraryPanel },
    { id: 'workout-recording', name: 'Workout Recording', component: WorkoutRecordingPanel },
    { id: 'community', name: 'Community', component: CommunityPanel },
    { id: 'social-media', name: 'Social Media', component: SocialMediaPanel },
    { id: 'health-data', name: 'Health Data', component: HealthDataPanel },
    { id: 'weight-management', name: 'Weight Management', component: WeightManagementPanel },
    { id: 'photo-progress', name: 'Photo Progress', component: PhotoProgressPanel },
    // { id: 'motivational', name: 'Motivational', component: MotivationalPanel },
    { id: 'soma-chat', name: 'Coach Chat', component: SomaChatPanel },
    { id: 'gamification', name: 'Awards', component: GamificationPanel },
    { id: 'admin-dashboard', name: 'Admin Dashboard', component: AdminDashboardPanel },
    { id: 'store', name: 'Store', component: StorePanel },
    { id: 'device-integration', name: 'Device Integration', component: DeviceIntegrationPanel },
    { id: 'wearable-verification', name: 'Wearable Verification', component: WearableVerificationPlugin },
    { id: 'recovery-verification', name: 'Recovery Verification', component: RecoveryVerificationPlugin },
  ];

  const mockUserProfile = {
    id: 'demo-user',
    name: 'Demo User',
    email: 'demo@sculptd.com',
    fitnessLevel: 'intermediate',
    goals: ['weight_loss', 'muscle_gain'],
    preferences: {
      workoutDuration: 45,
      equipment: ['dumbbells', 'bodyweight'],
      schedule: ['monday', 'wednesday', 'friday']
    }
  };

  const renderActiveComponent = () => {
    const activeComp = components.find(comp => comp.id === activeComponent);
    if (!activeComp) return null;

    const Component = activeComp.component;
    return (
      <ErrorBoundary fallback={<div className="p-4 text-red-600">Something went wrong loading this component.</div>}>
        <div className="h-full">
          <Component />
        </div>
      </ErrorBoundary>
    );
  };

  if (isLoading) {
    return <LoadingSpinner size="xl" variant="fitness" text="Loading SCULPTD Fitness App..." />;
  }

  return (
    <div className="min-h-screen sculptd-component" style={{ background: 'var(--color-background)' }}>
      {/* Demo Banner */}
      {showDemoBanner && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 text-center text-sm font-medium">
          🎯 DEMO MODE - This is a demonstration of SCULPTD Fitness App features
        </div>
      )}
      
      {/* Header */}
      <div className="sculptd-card border-0 rounded-none shadow-sm border-b">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 mr-3"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
              
              <img 
                src="/assets/sculptd-logo.png" 
                alt="SCULPTD" 
                className="h-12 md:h-16 w-auto"
                style={{ 
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}
              />
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-sm px-3 py-1 rounded-full" style={{ 
                background: 'var(--gradient-primary)',
                color: 'var(--color-white)'
              }}>
                16 Enhanced Components {isDemoMode() && '(Demo Mode)'}
              </div>
              <DemoModeToggle />
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-screen relative">
        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
          </div>
        )}

        {/* Sidebar Navigation */}
        <div className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:static absolute inset-y-0 left-0 z-50
          w-64 md:w-64 sculptd-card border-0 rounded-none shadow-lg border-r
          transition-transform duration-300 ease-in-out
        `} style={{ justifyContent: 'flex-start' }}>
          <div className="px-4 pt-4 pb-4 h-full overflow-y-auto">
            <nav className="space-y-1">
              {components.map((comp) => (
                <button
                  key={comp.id}
                  onClick={() => {
                    setActiveComponent(comp.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-3 md:py-2 rounded-md text-sm font-medium transition-all duration-200 hover:bg-opacity-10 touch-manipulation"
                  style={{
                    background: activeComponent === comp.id 
                      ? 'var(--gradient-primary)' 
                      : 'transparent',
                    color: activeComponent === comp.id 
                      ? 'var(--color-white)' 
                      : 'var(--color-text-muted)',
                    minHeight: '44px'
                  }}
                  onMouseEnter={(e) => {
                    if (activeComponent !== comp.id) {
                      (e.target as HTMLElement).style.background = 'rgba(58, 175, 169, 0.1)';
                      (e.target as HTMLElement).style.color = 'var(--color-text)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeComponent !== comp.id) {
                      (e.target as HTMLElement).style.background = 'transparent';
                      (e.target as HTMLElement).style.color = 'var(--color-text-muted)';
                    }
                  }}
                >
                  {comp.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden w-full md:w-auto">
          <div className="h-full p-3 md:p-6">
            <div className="sculptd-card h-full overflow-hidden">
              <div className="h-full overflow-auto">
                {renderActiveComponent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FitnessDemo;
